import { useState } from "react";
import { toast } from "sonner";
import { publicApi, formatPublicError, isValidMobile, honeypotOf } from "../../lib/publicApi";
import usePublicSettings from "../../lib/usePublicSettings";
import { useT } from "../../lib/i18n";
import { MapPin, Phone, Mail, Clock, MessageCircle } from "lucide-react";

import { tr } from "../../lib/i18n";
import { EnquireButton } from "../../components/EnquireDialog";
export default function ContactPage() {
  const { t } = useT();
  const { settings } = usePublicSettings();
  const [form, setForm] = useState({ name: "", mobile: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidMobile(form.mobile)) { toast.error(t("enquiry.mobile_invalid")); return; }
    setSending(true);
    try {
      await publicApi.post("/enquiries", { kind: "contact", ...form, honeypot: honeypotOf(e) });
      toast.success(t("enquiry.success"));
      setForm({ name: "", mobile: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatPublicError(err));
    } finally {
      setSending(false);
    }
  };

  const wa = settings?.whatsapp ? settings.whatsapp.replace(/[^\d]/g, "") : "";
  const lat = settings?.map_lat, lng = settings?.map_lng;

  return (
    <div data-testid="contact-page">
      <div className="vd-band on-dark">
        <div className="public-container" style={{ padding: "140px 20px 48px" }}>
          <div className="kicker" style={{ color: "rgba(255,255,255,0.6)" }}>{tr("x.get_in_touch")}</div>
          <h1 style={{ fontSize: "clamp(44px,5.6vw,88px)", fontWeight: 200, letterSpacing: "-0.03em", margin: "14px 0 0", color: "#fff" }}>
            {t("contact.title")}
          </h1>
        </div>
      </div>
      <div className="public-container" style={{ padding: "48px 20px 80px" }}>
      <div style={{ display: "grid", gap: 32, gridTemplateColumns: "1fr 1fr", marginTop: 0 }} className="contact-grid">
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {settings?.address ? (
            <div className="vd-priceblock">
              <MapPin size={20} color="var(--accent)" />
              <h3 style={{ margin: "8px 0 4px", fontFamily: "Outfit" }}>{tr("x.our_showroom")}</h3>
              <p style={{ color: "var(--pub-ink-soft)", fontSize: 14, margin: 0 }}>{settings.address}</p>
            </div>
          ) : null}

          {settings?.opening_hours ? (
            <div className="vd-priceblock">
              <Clock size={20} color="var(--accent)" />
              <h3 style={{ margin: "8px 0 4px", fontFamily: "Outfit" }}>{t("contact.opening_hours")}</h3>
              <p style={{ color: "var(--pub-ink-soft)", fontSize: 14, margin: 0, whiteSpace: "pre-wrap" }}>{settings.opening_hours}</p>
            </div>
          ) : null}

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {settings?.phone ? <a href={`tel:${settings.phone}`} className="chip" style={{ padding: 14, fontSize: 15 }}><Phone size={16} /> <bdi dir="ltr">{settings.phone}</bdi></a> : null}
            {wa ? <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="chip" style={{ padding: 14, fontSize: 15, background: "#dcfce7", color: "#14532d" }}><MessageCircle size={16} /> WhatsApp</a> : null}
            {settings?.email ? <a href={`mailto:${settings.email}`} className="chip" style={{ padding: 14, fontSize: 15 }}><Mail size={16} /> {settings.email}</a> : null}
          </div>

          {lat && lng ? (
            <iframe
              title="map"
              width="100%"
              height="260"
              style={{ border: 0, borderRadius: 16 }}
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.01}%2C${lat-0.01}%2C${lng+0.01}%2C${lat+0.01}&layer=mapnik&marker=${lat}%2C${lng}`}
            />
          ) : null}
        </div>

        <div className="vd-priceblock">
          <h3 style={{ margin: "0 0 12px", fontFamily: "Outfit" }}>{t("contact.send_message")}</h3>
          <div style={{ marginBottom: 16 }}><EnquireButton className="pub-cta dark big" /></div>
          <form className="enquiry-form" onSubmit={submit} data-testid="contact-form">
            <input type="text" name="website" tabIndex={-1} autoComplete="off" style={{ display: "none" }} data-testid="contact-honeypot" />
            <input placeholder={t("enquiry.your_name")} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
            <input placeholder={t("enquiry.mobile")} required inputMode="tel" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="contact-mobile" />
            <input placeholder={t("enquiry.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
            <textarea placeholder={t("enquiry.message")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
            <button type="submit" disabled={sending} data-testid="contact-submit">
              {sending ? t("enquiry.sending") : t("enquiry.submit")}
            </button>
          </form>
        </div>
      </div>
      </div>

      <style>{`@media (max-width: 900px) { .contact-grid { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}
